import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { Slider } from "../components/ui/slider";
import { Music, Search, Download, Volume2, ArrowLeft, Heart, Share2, Plus, Pause } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AMBIENT_OPTIONS, startAmbient, stopAmbient } from "../utils/ambientAudio";
import { generateQuoteCard, downloadQuoteCard } from "../utils/quoteCard";

const VOICE_GROUPS = [
  {
    label: "Female Voices",
    color: "text-purple-400",
    voices: [
      ["rachel", "Rachel — Calm & Neutral"],
      ["domi", "Domi — Strong & Confident"],
      ["bella", "Bella — Soft & Friendly"],
      ["elli", "Elli — Emotional & Expressive"],
      ["grace", "Grace — Calm Southern"],
      ["matilda", "Matilda — Warm & Pleasant"],
      ["nicole", "Nicole — Whisper-like"],
      ["charlotte", "Charlotte — Professional"],
      ["alice", "Alice — Confident"],
      ["lily", "Lily — Young & Energetic"],
    ],
  },
  {
    label: "Male Voices",
    color: "text-blue-400",
    voices: [
      ["clyde", "Clyde — Warm & Deep"],
      ["dave", "Dave — Conversational"],
      ["josh", "Josh — Deep & Authoritative"],
      ["adam", "Adam — Deep Narrator"],
      ["arnold", "Arnold — Strong & Commanding"],
      ["callum", "Callum — Calm & Reassuring"],
      ["daniel", "Daniel — News Anchor"],
      ["george", "George — British Sophisticated"],
      ["james", "James — Mature & Professional"],
      ["jeremy", "Jeremy — British Warm"],
      ["michael", "Michael — Authoritative"],
      ["thomas", "Thomas — Calm British"],
    ],
  },
  {
    label: "Special Voices",
    color: "text-amber-400",
    voices: [
      ["antoni", "Antoni — Versatile"],
      ["glinda", "Glinda — Mystical"],
      ["dorothy", "Dorothy — Storytelling"],
    ],
  },
];

const BrowsePage = () => {
  const navigate = useNavigate();
  const { user, setUser, userLoading } = useContext(UserContext);
  const [quotes, setQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [generatingAudio, setGeneratingAudio] = useState({});
  const [selectedVoice, setSelectedVoice] = useState("rachel");
  const [language, setLanguage] = useState("english");
  const [languages, setLanguages] = useState([{ code: "english", name: "English" }]);
  const [ambientKind, setAmbientKind] = useState("none");
  const [speed, setSpeed] = useState(1.0);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [playlists, setPlaylists] = useState([]);
  const [shareQuote, setShareQuote] = useState(null);
  const [sharePreview, setSharePreview] = useState(null);
  const [addToPlaylistAudio, setAddToPlaylistAudio] = useState(null);
  const [newPlaylistName, setNewPlaylistName] = useState("");
  const [showSubscription, setShowSubscription] = useState(false);
  const [processingSession, setProcessingSession] = useState(false);

  const audioRef = useRef(null);
  const lastAudioIdRef = useRef(null);

  // Process Google OAuth session_id from URL fragment
  useEffect(() => {
    const processSessionId = async () => {
      const hash = window.location.hash;
      if (hash && hash.includes("session_id=")) {
        setProcessingSession(true);
        const sessionId = hash.split("session_id=")[1].split("&")[0];

        try {
          const res = await axios.post(
            `${API}/auth/google/session`,
            {},
            { headers: { "X-Session-ID": sessionId }, withCredentials: true }
          );

          localStorage.setItem("token", res.data.token);
          setUser(res.data.user);
          toast.success("Welcome! Successfully logged in with Google");

          window.history.replaceState(null, "", window.location.pathname);
        } catch (error) {
          toast.error("Failed to authenticate with Google");
          navigate("/");
        } finally {
          setProcessingSession(false);
        }
      }
    };

    processSessionId();
  }, []);

  useEffect(() => {
    if (userLoading || processingSession) return;
    if (!user) {
      navigate("/");
      return;
    }

    axios.post(`${API}/seed-quotes`).finally(() => fetchQuotes());
    fetchLanguages();
    fetchFavorites();
    fetchPlaylists();
  }, [user, userLoading, processingSession]);

  useEffect(() => {
    filterQuotes();
  }, [quotes, category, search]);

  // Update playback rate when speed changes
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = speed;
  }, [speed]);

  // Update ambient when changed
  useEffect(() => {
    startAmbient(ambientKind);
    return () => stopAmbient();
  }, [ambientKind]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAmbient();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`);
      setQuotes(res.data);
      setFilteredQuotes(res.data);
    } catch (error) {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  const fetchLanguages = async () => {
    try {
      const res = await axios.get(`${API}/languages`);
      setLanguages(res.data);
    } catch (e) {}
  };

  const fetchFavorites = async () => {
    try {
      const res = await axios.get(`${API}/favorites`, { headers: authHeader() });
      setFavorites(new Set(res.data.map((f) => f.quote_id)));
    } catch (e) {}
  };

  const fetchPlaylists = async () => {
    try {
      const res = await axios.get(`${API}/playlists`, { headers: authHeader() });
      setPlaylists(res.data);
    } catch (e) {}
  };

  const filterQuotes = () => {
    let filtered = quotes;
    if (category !== "all") filtered = filtered.filter((q) => q.category === category);
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(
        (q) =>
          q.text.toLowerCase().includes(s) ||
          q.author.toLowerCase().includes(s) ||
          q.source.toLowerCase().includes(s)
      );
    }
    setFilteredQuotes(filtered);
  };

  const playAudio = (audioBase64) => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const audio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
    audio.playbackRate = speed;
    audioRef.current = audio;
    audio.play();
    audio.onended = () => setCurrentlyPlaying(null);
    return audio;
  };

  const generateAudio = async (quoteId) => {
    if (currentlyPlaying === quoteId && audioRef.current) {
      audioRef.current.pause();
      setCurrentlyPlaying(null);
      return;
    }

    if (user.subscription_status !== "active") {
      setShowSubscription(true);
      return;
    }

    setGeneratingAudio({ ...generatingAudio, [quoteId]: true });

    try {
      const res = await axios.post(
        `${API}/audio/generate`,
        { quote_id: quoteId, voice: selectedVoice, language },
        { headers: authHeader() }
      );

      lastAudioIdRef.current = res.data.id;
      playAudio(res.data.audio_base64);
      setCurrentlyPlaying(quoteId);
      toast.success("Playing");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate audio");
    } finally {
      setGeneratingAudio({ ...generatingAudio, [quoteId]: false });
    }
  };

  const downloadAudio = async (quoteId) => {
    if (user.subscription_status !== "active") {
      setShowSubscription(true);
      return;
    }

    try {
      const audioRes = await axios.post(
        `${API}/audio/generate`,
        { quote_id: quoteId, voice: selectedVoice, language },
        { headers: authHeader() }
      );

      const response = await axios.get(`${API}/audio/${audioRes.data.id}/download`, {
        headers: authHeader(),
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `quote_${quoteId}_${language}.mp3`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Audio downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download audio");
    }
  };

  const toggleFavorite = async (quoteId) => {
    try {
      const res = await axios.post(
        `${API}/favorites/toggle`,
        { quote_id: quoteId },
        { headers: authHeader() }
      );
      const next = new Set(favorites);
      if (res.data.favorited) next.add(quoteId);
      else next.delete(quoteId);
      setFavorites(next);
      toast.success(res.data.favorited ? "Added to favorites" : "Removed from favorites");
    } catch (e) {
      toast.error("Failed to update favorite");
    }
  };

  const openShare = (quote) => {
    setShareQuote(quote);
    const dataUrl = generateQuoteCard(quote);
    setSharePreview(dataUrl);
  };

  const downloadShare = () => {
    if (!shareQuote) return;
    downloadQuoteCard(shareQuote);
    toast.success("Quote card downloaded");
  };

  const shareToTwitter = () => {
    if (!shareQuote) return;
    const text = `"${shareQuote.text}" — ${shareQuote.author}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.origin)}`;
    window.open(url, "_blank", "noopener");
  };

  const copyShareText = async () => {
    if (!shareQuote) return;
    const text = `"${shareQuote.text}" — ${shareQuote.author}\n\n${window.location.origin}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (e) {
      toast.error("Could not copy");
    }
  };

  const openAddToPlaylist = async (quoteId) => {
    if (user.subscription_status !== "active") {
      setShowSubscription(true);
      return;
    }
    try {
      // Ensure audio exists
      const audioRes = await axios.post(
        `${API}/audio/generate`,
        { quote_id: quoteId, voice: selectedVoice, language },
        { headers: authHeader() }
      );
      setAddToPlaylistAudio(audioRes.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to prepare audio");
    }
  };

  const addAudioToPlaylist = async (playlistId) => {
    if (!addToPlaylistAudio) return;
    try {
      await axios.put(
        `${API}/playlists/${playlistId}`,
        { audio_id: addToPlaylistAudio, action: "add" },
        { headers: authHeader() }
      );
      toast.success("Added to playlist");
      setAddToPlaylistAudio(null);
      fetchPlaylists();
    } catch (e) {
      toast.error("Failed to add to playlist");
    }
  };

  const createPlaylistAndAdd = async () => {
    const name = newPlaylistName.trim();
    if (!name) return;
    try {
      const res = await axios.post(
        `${API}/playlists`,
        { name },
        { headers: authHeader() }
      );
      setNewPlaylistName("");
      await fetchPlaylists();
      if (addToPlaylistAudio) {
        await addAudioToPlaylist(res.data.id);
      } else {
        toast.success("Playlist created");
      }
    } catch (e) {
      toast.error("Failed to create playlist");
    }
  };

  if (loading || processingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-white text-sm pulse uppercase tracking-widest">
          {processingSession ? "Authenticating..." : "Loading..."}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-sm border-b border-amber-200/15">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/")}
              className="text-xs uppercase tracking-[0.25em] text-amber-200/80 hover:text-amber-200 transition-colors font-light flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Home
            </button>
            <span className="script-font text-3xl gold-text">Browse</span>
          </div>

          <div className="flex items-center space-x-6">
            {user?.subscription_status === "active" ? (
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/60 cinzel">
                {user.subscription_tier === "lifetime" ? "Lifetime · Active" : "Premium · Active"}
              </div>
            ) : (
              <Button
                data-testid="subscribe-header-btn"
                onClick={() => setShowSubscription(true)}
                className="gold-btn text-xs uppercase tracking-wider font-medium"
              >
                Subscribe
              </Button>
            )}
            <Button
              data-testid="library-btn"
              onClick={() => navigate("/library")}
              className="gold-outline-btn text-xs uppercase tracking-wider font-medium bg-transparent"
            >
              Library
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-32 pb-12 px-8">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="relative border border-amber-200/20 p-8 mb-8 bg-gradient-to-b from-zinc-950/60 to-black/60 backdrop-blur-sm">
            <span className="absolute top-0 left-0 w-5 h-5 border-t border-l border-amber-200/70 pointer-events-none" />
            <span className="absolute top-0 right-0 w-5 h-5 border-t border-r border-amber-200/70 pointer-events-none" />
            <span className="absolute bottom-0 left-0 w-5 h-5 border-b border-l border-amber-200/70 pointer-events-none" />
            <span className="absolute bottom-0 right-0 w-5 h-5 border-b border-r border-amber-200/70 pointer-events-none" />

            <div className="grid md:grid-cols-3 gap-6 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-amber-200/50" />
                <Input
                  data-testid="search-input"
                  type="text"
                  placeholder="Search quotes..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-black border-amber-200/25 text-white placeholder:text-amber-200/30 focus:border-amber-200 text-sm"
                />
              </div>

              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="category-select" className="bg-black border-amber-200/25 text-white text-sm focus:ring-amber-200/30">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-amber-200/25">
                  <SelectItem value="all" className="text-white">All Categories</SelectItem>
                  <SelectItem value="religious" className="text-white">Religious Texts</SelectItem>
                  <SelectItem value="metaphysical" className="text-white">Metaphysical</SelectItem>
                  <SelectItem value="consciousness" className="text-white">Consciousness</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                <SelectTrigger data-testid="voice-select" className="bg-black border-amber-200/25 text-white text-sm focus:ring-amber-200/30">
                  <SelectValue placeholder="Voice" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-amber-200/25 max-h-96 overflow-y-auto">
                  {VOICE_GROUPS.map((g) => (
                    <div key={g.label}>
                      <div className={`px-2 py-1.5 text-[10px] font-semibold ${g.color} uppercase tracking-[0.3em] mt-2 cinzel`}>
                        {g.label}
                      </div>
                      {g.voices.map(([val, label]) => (
                        <SelectItem key={val} value={val} className="text-white">
                          {label}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 2: Language, Background Music, Speed */}
            <div className="grid md:grid-cols-3 gap-6 pt-6 border-t border-amber-200/15">
              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-amber-200/60 mb-2 cinzel">Language</div>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="language-select" className="bg-black border-amber-200/25 text-white text-sm focus:ring-amber-200/30">
                    <SelectValue placeholder="Language" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-amber-200/25">
                    {languages.map((l) => (
                      <SelectItem key={l.code} value={l.code} className="text-white">
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-amber-200/60 mb-2 cinzel">Background Music</div>
                <Select value={ambientKind} onValueChange={setAmbientKind}>
                  <SelectTrigger data-testid="ambient-select" className="bg-black border-amber-200/25 text-white text-sm focus:ring-amber-200/30">
                    <SelectValue placeholder="Ambient" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-amber-200/25">
                    {AMBIENT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} className="text-white">
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-[10px] uppercase tracking-[0.4em] text-amber-200/60 mb-2 flex justify-between cinzel">
                  <span>Speed</span>
                  <span className="text-amber-200" data-testid="speed-value">{speed.toFixed(2)}x</span>
                </div>
                <Slider
                  data-testid="speed-slider"
                  min={0.5}
                  max={2}
                  step={0.05}
                  value={[speed]}
                  onValueChange={(v) => setSpeed(v[0])}
                  className="mt-4"
                />
              </div>
            </div>
          </div>

          {/* Quotes Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredQuotes.map((quote, index) => {
              const isFav = favorites.has(quote.id);
              return (
                <Card
                  key={quote.id}
                  data-testid={`quote-card-${index}`}
                  className="relative bg-gradient-to-b from-zinc-950 to-black border border-amber-200/20 hover:border-amber-200/60 transition-all duration-500 fade-in-up overflow-hidden group"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {/* Corner ornaments */}
                  <span className="absolute top-0 left-0 w-6 h-6 border-t border-l border-amber-200/60 pointer-events-none" />
                  <span className="absolute top-0 right-0 w-6 h-6 border-t border-r border-amber-200/60 pointer-events-none" />
                  <span className="absolute bottom-0 left-0 w-6 h-6 border-b border-l border-amber-200/60 pointer-events-none" />
                  <span className="absolute bottom-0 right-0 w-6 h-6 border-b border-r border-amber-200/60 pointer-events-none" />
                  {/* Subtle gold glow on hover */}
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                    style={{
                      background: "radial-gradient(circle at 50% 0%, rgba(228,178,89,0.08), transparent 70%)",
                    }}
                  />

                  <CardContent className="relative p-8 space-y-6">
                    <div className="flex justify-between items-start">
                      <div className="flex space-x-3">
                        <button
                          data-testid={`favorite-btn-${index}`}
                          onClick={() => toggleFavorite(quote.id)}
                          className="text-amber-200/50 hover:text-rose-400 transition-colors"
                          aria-label="Toggle favorite"
                        >
                          <Heart className={`w-4 h-4 ${isFav ? "fill-rose-400 text-rose-400" : ""}`} />
                        </button>
                        <button
                          data-testid={`share-btn-${index}`}
                          onClick={() => openShare(quote)}
                          className="text-amber-200/50 hover:text-amber-200 transition-colors"
                          aria-label="Share quote"
                        >
                          <Share2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`add-playlist-btn-${index}`}
                          onClick={() => openAddToPlaylist(quote.id)}
                          className="text-amber-200/50 hover:text-amber-200 transition-colors"
                          aria-label="Add to playlist"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.3em] text-amber-200/50 cinzel">
                        {quote.category}
                      </span>
                    </div>

                    <div className="min-h-[120px] relative">
                      <span className="absolute -top-2 -left-1 text-4xl text-amber-200/30 leading-none font-serif italic">&ldquo;</span>
                      <p className="text-sm text-gray-200 italic leading-relaxed font-light pt-4 pl-4">
                        {quote.text}
                      </p>
                    </div>

                    <div className="border-t border-amber-200/15 pt-6 space-y-2">
                      <p className="text-xs uppercase tracking-[0.3em] text-amber-100/90 cinzel">{quote.author}</p>
                      <p className="text-[11px] text-amber-200/40 font-light italic">{quote.source}</p>
                    </div>

                    <div className="flex space-x-3 pt-2">
                      <Button
                        data-testid={`play-audio-btn-${index}`}
                        onClick={() => generateAudio(quote.id)}
                        disabled={generatingAudio[quote.id]}
                        className="flex-1 gold-btn text-xs uppercase tracking-wider font-medium"
                      >
                        {generatingAudio[quote.id] ? (
                          "Generating..."
                        ) : currentlyPlaying === quote.id ? (
                          <>
                            <Pause className="w-3 h-3 mr-2" />
                            Pause
                          </>
                        ) : (
                          <>
                            <Music className="w-3 h-3 mr-2" />
                            Play
                          </>
                        )}
                      </Button>

                      <Button
                        data-testid={`download-audio-btn-${index}`}
                        onClick={() => downloadAudio(quote.id)}
                        className="gold-outline-btn bg-transparent"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredQuotes.length === 0 && (
            <div className="text-center py-24">
              <p className="text-gray-600 text-sm uppercase tracking-wider font-light">No quotes found</p>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Dialog */}
      <Dialog open={showSubscription} onOpenChange={setShowSubscription}>
        <DialogContent className="bg-black border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="script-font text-4xl text-center">Upgrade Required</DialogTitle>
            <DialogDescription className="text-center text-gray-500 text-xs">
              Audio generation needs a Premium, Pro or Lifetime plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-8 mt-6">
            <p className="text-center text-gray-300 text-sm">
              Audio generation is available on Premium, Pro and Lifetime plans
            </p>
            <Button
              data-testid="view-pricing-btn"
              onClick={() => navigate("/pricing")}
              className="w-full bg-white text-black hover:bg-gray-200 py-6 text-sm uppercase tracking-wider font-light"
            >
              View Pricing Plans
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={!!shareQuote} onOpenChange={(open) => !open && setShareQuote(null)}>
        <DialogContent className="bg-black border border-white/10 text-white max-w-lg" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="script-font text-3xl text-center">Share</DialogTitle>
            <DialogDescription className="text-center text-gray-500 text-xs">
              Download a branded quote card or post to your favorite channel
            </DialogDescription>
          </DialogHeader>
          {sharePreview && (
            <img
              src={sharePreview}
              alt="Quote card preview"
              className="w-full border border-white/10 rounded"
              data-testid="share-preview-img"
            />
          )}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <Button
              data-testid="share-download-btn"
              onClick={downloadShare}
              className="bg-white text-black hover:bg-gray-200 text-xs uppercase tracking-wider font-light"
            >
              <Download className="w-3 h-3 mr-2" /> PNG
            </Button>
            <Button
              data-testid="share-twitter-btn"
              onClick={shareToTwitter}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 text-xs uppercase tracking-wider font-light"
            >
              X / Twitter
            </Button>
            <Button
              data-testid="share-copy-btn"
              onClick={copyShareText}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/5 text-xs uppercase tracking-wider font-light"
            >
              Copy
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add to Playlist Dialog */}
      <Dialog open={!!addToPlaylistAudio} onOpenChange={(open) => !open && setAddToPlaylistAudio(null)}>
        <DialogContent className="bg-black border border-white/10 text-white max-w-md" data-testid="playlist-dialog">
          <DialogHeader>
            <DialogTitle className="script-font text-3xl text-center">Add to Playlist</DialogTitle>
            <DialogDescription className="text-center text-gray-500 text-xs">
              Choose an existing playlist or create a new one
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {playlists.length > 0 ? (
              playlists.map((p, i) => (
                <button
                  key={p.id}
                  data-testid={`select-playlist-${i}`}
                  onClick={() => addAudioToPlaylist(p.id)}
                  className="w-full text-left border border-white/10 hover:border-white/30 px-4 py-3 text-sm font-light transition-colors"
                >
                  {p.name}
                  <span className="text-gray-600 text-xs ml-2">({(p.audio_ids || []).length} tracks)</span>
                </button>
              ))
            ) : (
              <p className="text-gray-600 text-xs text-center">No playlists yet — create one below</p>
            )}

            <div className="flex space-x-2 pt-4 border-t border-white/10">
              <Input
                data-testid="new-playlist-name-input"
                placeholder="New playlist name"
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                className="bg-black border-white/20 text-white text-sm"
              />
              <Button
                data-testid="create-playlist-btn"
                onClick={createPlaylistAndAdd}
                disabled={!newPlaylistName.trim()}
                className="bg-white text-black hover:bg-gray-200 text-xs uppercase tracking-wider font-light"
              >
                Create
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrowsePage;
